import { useEffect, useState, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, FileText, FileVideo, File } from "lucide-react";
import { useLocation } from "react-router-dom";
import Distinctions from "./Distinctions";
import AppTour from "../../../../components/AppTour/AppTour";
import { contentViewerTourSteps } from "../../../../components/AppTour/ModuleTourSteps";
import { getContentById } from "../../../../api/professoru/contents.api";
import { useContentById, useContentByModule } from "../../../../queries/professoru/contents.api";
import MusicLoader from "../../DataAnalyst/sections/components/MusicLoader";
import { useProfessorUActivityTracker } from "../../../../hooks/useProfessorUActivityTracker";
import useVideoProgress from "../../../../hooks/useVideoProgress";
import useYouTubePlayer from "../../../../hooks/useYouTubePlayer";
import useScrollProgress from "../../../../hooks/useScrollProgress";

const getContentCategory = (content, pageContent = "") => {
    const type = content?.type;
    const page = String(pageContent).toLowerCase();

    if (type === "lesson" || type === "lessons" || page.includes("lesson")) return "lesson";
    if (type === "interview" || type === "talk" || page.includes("interview") || page.includes("talk")) return "talk";
    if (["guide", "walkthroughs", "troubleshooting", "walkthrough"].includes(type) || page.includes("guide") || page.includes("walkthrough")) return "guide";
    return "guide";
};

const getYouTubeVideoId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
};

export default function ContentViewer() {
    const [runTour, setRunTour] = useState(false);
    const location = useLocation();
    const [htmlContent, setHtmlContent] = useState('');
    const params = new URLSearchParams(location.search);
    const pageContent = params.get("content");
    const moduleId = params.get("moduleId")
    const contentId = params.get("contentId")
    const [activeContentId, setActiveContentId] = useState(contentId);
    const { data: moduleData, isLoading: loadingModule } = useContentByModule(moduleId)
    const { data: contentData, isLoading: loadingContent } = useContentById(activeContentId)
    const contentUrl = contentData?.content?.mediaUrl || ''

    const videoRef = useRef(null);
    const htmlContainerRef = useRef(null);
    const pdfTimerRef = useRef(null);
    const pdfCompletedRef = useRef(false);

    const {
        trackVideoCompleted,
        trackGuideRead,
        trackTalkWatched,
    } = useProfessorUActivityTracker();

    // Find current index based on contentId
    const contents = Array.isArray(moduleData) ? moduleData : [];
    const currentIndex = contents.findIndex(item => item._id === activeContentId);
    const currentContent = currentIndex !== -1 ? contents[currentIndex] : null;

    const handleContentCompleted = useCallback(() => {
        if (!currentContent) return;
        const contentId = currentContent._id;
        const moduleId = currentContent.moduleId;
        const contentType = currentContent.type;
        const category = getContentCategory(currentContent, pageContent);

        if (category === "lesson") {
            trackVideoCompleted(contentId, moduleId, contentType);
        } else if (category === "talk") {
            trackTalkWatched(contentId, moduleId, contentType);
        } else {
            trackGuideRead(contentId, moduleId, contentType);
        }
    }, [currentContent, pageContent, trackVideoCompleted, trackTalkWatched, trackGuideRead]);

    const youtubeVideoId = contentUrl ? getYouTubeVideoId(contentUrl) : null;
    console.log("[ContentViewer] contentUrl", contentUrl, "youtubeVideoId", youtubeVideoId);
    const { containerRef: youtubeContainerRef, playerRef } = useYouTubePlayer(youtubeVideoId);

    const getVideoElement = useCallback(() => videoRef.current, []);
    const getPlayer = useCallback(() => playerRef.current, [playerRef]);

    useVideoProgress({
        getVideoElement,
        getPlayer,
        onComplete: handleContentCompleted,
        threshold: 0.9,
        contentId: activeContentId,
    });

    useScrollProgress({
        ref: htmlContainerRef,
        onComplete: handleContentCompleted,
        threshold: 0.8,
        minTimeMs: 15000,
    });

    useEffect(() => {
        setActiveContentId(contentId);
    }, [contentId]);

    const next = () => {
        if (currentIndex < contents.length - 1) {
            const nextContent = contents[currentIndex + 1];
            setActiveContentId(nextContent._id);
            // navigate(`?content=${pageContent}&moduleId=${moduleId}&contentId=${nextContent._id}`);
        }
    };

    const prev = () => {
        if (currentIndex > 0) {
            const prevContent = contents[currentIndex - 1];
            setActiveContentId(prevContent._id);
            // navigate(`?content=${pageContent}&moduleId=${moduleId}&contentId=${prevContent._id}`);
        }
    };

    const openFile = (url) => {
        window.open(url, "_blank");
    };

    const shouldFetchAsHtml = (url) => {
        if (!url) return false;
        const lower = url.toLowerCase();
        if (/youtube\.com\/watch|youtu\.be\/|youtube\.com\/embed/.test(lower)) return false;
        if (/\.(mp4|webm|avi|mov|mkv)(\?|$)/.test(lower)) return false;
        if (/\.(pdf|doc|docx)(\?|$)/.test(lower)) return false;
        return true;
    };

    useEffect(() => {
        if (!contentUrl || !shouldFetchAsHtml(contentUrl)) {
            setHtmlContent('');
            return;
        }
        fetch(contentUrl)
            .then(res => res.text())
            .then(data => setHtmlContent(data))
            .catch(err => console.error('Failed to load HTML:', err));
    }, [contentUrl]);

    useEffect(() => {
        pdfCompletedRef.current = false;
        if (pdfTimerRef.current) {
            clearTimeout(pdfTimerRef.current);
            pdfTimerRef.current = null;
        }
    }, [activeContentId, contentUrl]);

    const renderIcon = (type) => {
        switch (type) {
            case "pdf":
                return <FileText className="text-red-500 w-10 h-10" />;
            case "video":
                return <FileVideo className="text-theme_clr1 w-10 h-10" />;
            case "doc":
                return <File className="text-indigo-500 w-10 h-10" />;
            default:
                return <File className="w-10 h-10" />;
        }
    };

    // Detect content type and return appropriate component
    const getYouTubeEmbedUrl = (url) => {
        const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
        const match = url.match(youtubeRegex);
        return match ? `https://www.youtube.com/embed/${match[1]}` : null;
    };

    const detectContentType = (url) => {
        if (!url) return null;

        const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();

        if (getYouTubeEmbedUrl(url)) return 'youtube';

        if (cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm') || cleanUrl.endsWith('.avi')) return 'video';
        if (cleanUrl.endsWith('.pdf')) return 'pdf';
        if (cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx')) return 'document';
        if (cleanUrl.endsWith('.html') || cleanUrl.endsWith('.htm')) return 'html';

        // If URL starts with http and has no file extension at the end, assume HTML
        if (cleanUrl.startsWith('http') && !/\.[a-zA-Z0-9]{2,6}$/.test(cleanUrl)) return 'html';

        return 'text';
    };

    const isLoading = loadingContent || loadingModule;

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                    <MusicLoader />
                </div>
            )
        }
        if (!contentUrl) {
            return (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                    No content available
                </div>
            );
        }

        const contentType = detectContentType(contentUrl);
        const sanitizedHtml = htmlContent
            .replace(/<style[\s\S]*?<\/style>/gi, '')
            .replace(/onclick="[^"]*"/gi, '');

        switch (contentType) {
            case 'youtube':
                return (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <div
                            ref={youtubeContainerRef}
                            className="w-full h-full"
                            style={{ minHeight: '400px' }}
                        />
                    </div>
                );

            case 'video':
                return (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <video
                            ref={videoRef}
                            width="100%"
                            height="100%"
                            controls
                            style={{ maxHeight: '400px', objectFit: 'contain' }}
                        >
                            <source src={contentUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                );

            case 'pdf':
                return (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <iframe
                            title={currentContent?.title || "PDF content"}
                            src={contentUrl}
                            width="100%"
                            height="100%"
                            style={{ minHeight: '400px' }}
                            onLoad={() => {
                                if (pdfCompletedRef.current) return;
                                pdfTimerRef.current = setTimeout(() => {
                                    pdfCompletedRef.current = true;
                                    handleContentCompleted();
                                }, 15000);
                            }}
                        />
                    </div>
                );

            case 'document':
                return (
                    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                        <FileText className="text-gray-400 w-16 h-16 mb-4" />
                        <p className="text-gray-600 mb-4">Document Preview</p>
                        <a
                            href={contentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-theme_clr1 text-white px-6 py-2 rounded-lg hover:opacity-80 transition"
                            onClick={handleContentCompleted}
                        >
                            Download Document
                        </a>
                    </div>
                );

            case 'html':
                return (
                    <div ref={htmlContainerRef} className="flex-1 overflow-auto p-6 bg-white">
                        <div className="max-w-4xl mx-auto">
                            <div
                                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                                className="guide-content"
                            />
                        </div>
                    </div>
                );

            default:
                // Text content
                return (
                    <div className="flex-1 overflow-auto p-6">
                        <div className="text-gray-800 whitespace-pre-wrap break-words">
                            {contentUrl}
                        </div>
                    </div>
                );
        }
    };

    useEffect(() => {
        if (location.hash) {
            setRunTour(true)
        }
    }, [location.hash])

    return (
        <div className="DataAnalyst-page py-5">
            <AppTour
                tourSteps={contentViewerTourSteps}
                runTour={runTour}
                setRunTour={setRunTour}
                tourName="professoru-content-viewer"
                moduleId={moduleId || "professoru-content-viewer"}
            />
            <div className="container max-w-full">
                <Distinctions
                    title={pageContent}
                    inputPlaceholder={"What do you want to achieve right now?"}
                />
                <div className="w-full bg-theme_clr1 rounded-2xl p-6 flex flex-col items-center mt-4" data-tour="content-viewer">

                    {/* Title Pill */}
                    <div className="bg-theme_clr2 text-white px-8 py-2 rounded-lg font-semibold mb-6">
                        {isLoading && !currentContent?.title ? "Loading..." : (currentContent?.title || pageContent || "Untitled")}
                    </div>

                    {/* Content Area */}
                    <div className="w-full bg-gray-200 rounded-xl h-[500px] relative overflow-hidden flex flex-col">
                        {/* Content Renderer */}
                        {renderContent()}
                    </div>

                    {/* Bottom Center Arrows */}
                    <div className="relative z-10 flex w-full min-h-[54px] flex-shrink-0 justify-center items-center gap-16 mt-4" data-tour="content-navigation">

                        {/* LEFT */}
                        <button
                            onClick={prev}
                            disabled={currentIndex <= 0}
                            className={`p-3 rounded-full transition
        ${currentIndex <= 0
                                    ? "text-gray-400 cursor-not-allowed"
                                    : "text-white hover:bg-gray-300"
                                }`}
                        >
                            <ChevronLeft size={30} />
                        </button>

                        {/* RIGHT */}
                        <button
                            onClick={next}
                            disabled={currentIndex === -1 || currentIndex >= contents.length - 1}
                            className={`p-3 rounded-full transition
        ${currentIndex === -1 || currentIndex >= contents.length - 1
                                    ? "text-gray-400 cursor-not-allowed"
                                    : "text-white hover:bg-gray-300"
                                }`}
                        >
                            <ChevronRight size={30} />
                        </button>

                    </div>
                </div>
            </div>
        </div>
    );
}